import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Row, Col, Form } from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";
import { omit } from "lodash";
import { Link, useNavigate } from "react-router-dom";
import Radio from "@mui/material/Radio";
import {
  Button,
  CircularProgress,
  FormControlLabel,
  FormLabel,
  RadioGroup,
} from "@mui/material";

import { useLazyLocationByPincodeQuery } from "apis/Location";
import { usePostRegisterMutation } from "apis/User";
import { useRequestOTPMutation } from "apis/OTP";

import Modal from "Components/Common/Modal";
import InputFieldText from "Components/Common/InputFieldText";
import SelectField from "Components/Common/SelectField";
import { salutationOptions } from "common/enums";

import "react-toastify/dist/ReactToastify.css";
import { fireSafetyHomeRoute, homeRoute } from "Routes/constants";
import { onlyNumberKeys } from "utils";
import VerifyEmailModal from "Components/Common/VerifyEmailModal";
import { otpVerificationType, regexForName } from "common/constant";
import { usePostRegisterConsumerMutation } from "apis/FireSafety";

const requestOTPType = otpVerificationType["Verification"];

const SignupModal = ({ open, toggle, isFireSafety }) => {
  const navigate = useNavigate();
  const [isOTPSent, setIsOTPSent] = useState(false);

  const [requestOTP, { isLoading: isRequestOTPLoading }] =
    useRequestOTPMutation();

  const [register, { isLoading }] = usePostRegisterMutation();

  const [registerFireSafetyConsumer, { isLoading: isConsumerRegistering }] =
    usePostRegisterConsumerMutation();

  const [getLocation] = useLazyLocationByPincodeQuery();
  const [pinCodeError, setPinCodeError] = useState("");

  const registerValidationSchema = Yup.object({
    salutation: Yup.string()
      .required("Please select your salutation")
      .nullable(),
    firstName: Yup.string()
      .matches(regexForName, "Please enter valid first name")
      // .min(3, "First name must contains more then two characters")
      .required("Please enter your first name"),
    middleName: Yup.string().matches(
      regexForName,
      "Please enter valid middle name"
    ),
    lastName: Yup.string().matches(
      regexForName,
      "Please enter valid last name"
    ),
    // .min(3, "Last name contains more then two characters")
    addressLine1: Yup.string().required("Please enter your address"),
    addressType: Yup.string(),
    city: Yup.object({
      id: Yup.number().required("City is required"),
    }),
    pincode: Yup.string()
      .matches(/^\d{6}$/, "Please enter valid pincode")
      .required("Please enter your pincode"),
    email: Yup.string()
      .email("Please enter valid email")
      .required("Please enter your email"),

    password: Yup.string().required("Please enter your password"),
    mobileNumber: Yup.string()
      .matches(/^\d{10}$/, "Please enter valid phone number")
      .required("Please enter your phone number"),
  });

  const formik = useFormik({
    initialValues: {
      salutation: null,
      firstName: "",
      middleName: "",
      lastName: "",
      addressLine1: "",
      addressLine2: "",
      addressType: "work",
      city: "",
      pincode: "",
      designation: "",
      email: "",
      mobileNumber: "",
      password: "",
      isEmailVerified: false,
    },
    validationSchema: registerValidationSchema,
    onSubmit: () => {
      generateOTP();
    },
  });

  const {
    values: { pincode, email, mobileNumber },
    setFieldValue,
  } = formik;

  const generateOTP = async () => {
    try {
      await requestOTP(
        {
          email: encodeURIComponent(email),
          mobileNumber,
          type: requestOTPType,
        },
        "",
        isFireSafety
      );
      setIsOTPSent(true);
    } catch (error) {
      setIsOTPSent(false);
    }
  };

  useEffect(() => {
    (async () => {
      if (pincode.toString().length === 6) {
        try {
          const res = await getLocation(pincode);
          setFieldValue("city", res.data.city);
          setPinCodeError("");
        } catch (error) {
          setFieldValue("city", "");
          setPinCodeError("Please enter a valid pincode");
        }
      }
    })();
  }, [pincode, getLocation, setFieldValue]);

  return (
    <>
      {(isRequestOTPLoading || isOTPSent) && (
        <VerifyEmailModal
          isOpen={isOTPSent}
          isFireSafety={isFireSafety}
          handleClose={() => setIsOTPSent(false)}
          handleOTPverificationSuccess={async () => {
            try {
              const payload = {
                ...formik.values,
                cityId: formik.values.city?.id ?? "",
                mobileNumber: `${formik.values.mobileNumber}`,
              };
              const registerObj = omit(payload, ["isEmailVerified", "city"]);
              if (!pinCodeError) {
                if (isFireSafety) {
                  await registerFireSafetyConsumer(registerObj);
                } else {
                  await register(registerObj);
                }
              }
              toggle();
              navigate(
                window.location.href.includes("fire-safety")
                  ? fireSafetyHomeRoute
                  : homeRoute,
                { replace: true }
              );
            } catch (error) {}
            setFieldValue("isEmailVerified", true);
          }}
          handleOTPVerificationFailed={() => {
            setFieldValue("isEmailVerified", false);
          }}
          email={email}
          mobileNumber={mobileNumber}
          isLoading={isRequestOTPLoading}
        />
      )}
      <Modal
        isOpen={open}
        handleClose={toggle}
        className="register-modal-wrapper"
        title="Registration"
      >
        <>
          <Form onSubmit={formik.handleSubmit}>
            <Row>
              <Col md={6}>
                <SelectField
                  label="Salutation*"
                  name="salutation"
                  formik={formik}
                  options={salutationOptions || []}
                  placeholder="Select Salutation"
                />
              </Col>
              <Col md={6}>
                <InputFieldText
                  label="First Name*"
                  name="firstName"
                  formik={formik}
                  placeholder="Enter name"
                />
              </Col>
              <Col md={6}>
                <InputFieldText
                  name="middleName"
                  placeholder="Enter Middle Name"
                  label="Middle Name"
                  formik={formik}
                />
              </Col>
              <Col md={6}>
                <InputFieldText
                  name="lastName"
                  placeholder="Enter Last Name"
                  label="Last Name"
                  formik={formik}
                />
              </Col>
              <Col md={6}>
                <InputFieldText
                  name="addressLine1"
                  placeholder="Enter Address 1"
                  label="Address 1*"
                  formik={formik}
                />
              </Col>
              <Col md={6}>
                <InputFieldText
                  name="addressLine2"
                  placeholder="Enter Address 2"
                  label="Address 2"
                  formik={formik}
                />
              </Col>
              <Col md={6}>
                <InputFieldText
                  label="Designation"
                  name="designation"
                  formik={formik}
                  placeholder="Enter Designation"
                />
              </Col>
              <Row className="mb-3">
                <Col className="d-flex align-items-center">
                  <FormLabel id="address-type" className="pe-4">
                    Address Type
                  </FormLabel>
                  <RadioGroup
                    row
                    aria-labelledby="address-type"
                    name="addressType"
                    value={formik.values.addressType}
                    onChange={formik.handleChange}
                  >
                    <FormControlLabel
                      value="work"
                      control={<Radio />}
                      label="Work"
                    />
                    <FormControlLabel
                      value="residential"
                      control={<Radio />}
                      label="Residential"
                    />
                  </RadioGroup>
                </Col>
              </Row>
              <Col md={6}>
                <InputFieldText
                  name="pincode"
                  type="number"
                  InputProps={{
                    inputProps: { min: 0, onKeyDown: onlyNumberKeys },
                  }}
                  placeholder="Enter Pincode"
                  label="Pincode*"
                  formik={formik}
                  errorMessage={pinCodeError}
                />
              </Col>
              <Col md={6}>
                <InputFieldText
                  label={"City*"}
                  name="city.name"
                  formik={formik}
                  placeholder="City"
                  disabled
                />
              </Col>
              <Col md={6}>
                <InputFieldText
                  label="Email*"
                  name="email"
                  formik={formik}
                  type="email"
                  placeholder="Enter Email"
                />
              </Col>
              <Col md={6}>
                <InputFieldText
                  name="mobileNumber"
                  type="number"
                  InputProps={{
                    inputProps: { min: 0, onKeyDown: onlyNumberKeys },
                  }}
                  placeholder="Enter Phone Number"
                  label="Phone Number*"
                  formik={formik}
                />
              </Col>
              <Col sm={6}>
                <InputFieldText
                  name="password"
                  type="password"
                  placeholder="Enter Password"
                  autoComplete="new-password"
                  label="Password*"
                  formik={formik}
                />
              </Col>
            </Row>
            <Button
              variant="contained"
              className="modal-btn semibold-font"
              type="submit"
              disabled={isLoading || isConsumerRegistering}
            >
              Register
              {isLoading && <CircularProgress size={20} />}
            </Button>
          </Form>
          <p className="regular-font pt-5 text-center">
            Already have an account ?
            <Link
              to={homeRoute}
              className="medium-font ps-2 sign-in-text"
              onClick={toggle}
            >
              Log In
            </Link>
          </p>
        </>
      </Modal>
    </>
  );
};

SignupModal.propTypes = {
  open: PropTypes.bool,
  toggle: PropTypes.func,
  isFireSafety: PropTypes.bool,
};

export default SignupModal;
